import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { fetchServiceSectionItems } from '@/api/cmsService'
import { cmsContactField } from '@/api/helpers'
import { serviceCardTargetFromItem } from '@/api/servicePageResolver'
import type { ServiceItem } from '@/api/types'
import { cmsEllipsis } from '@/styles/cmsText'
import { FOOTER_LINKS } from '@/lib/constants'
import { colors } from '@/styles/colors'
import { Mail, Phone, MapPin } from 'lucide-react'

interface FooterProps {
  isMobile: boolean
  cmsReady?: boolean
  contactInfo?: {
    email?: string
    phone?: string
    address?: string
  }
  /** Same service tickets as homepage Services slider (`GET /api/service` items). */
  serviceItems?: ServiceItem[]
}

const FALLBACK_FOOTER_SERVICES: ServiceItem[] = [
  { id: 1, title: 'Website Development & Digital Presence', description: '', icon: '', link: '', slug: 'web-development', order: 1 },
  { id: 2, title: 'eCommerce Solutions', description: '', icon: '', link: '', slug: 'ecommerce', order: 2 },
  { id: 3, title: 'Odoo ERP Solutions', description: '', icon: '', link: '', slug: 'odoo-erp', order: 3 },
  { id: 4, title: 'AI-Powered Solutions', description: '', icon: '', link: '', slug: 'ai-solutions', order: 4 },
]

const FOOTER_CONTACT_ICON: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '6.857px',
  width: '32px',
  height: '32px',
  minWidth: '32px',
  background: colors.primary[750],
  borderRadius: '18.286px',
  flexShrink: 0,
}

const FOOTER_CONTACT_ROW: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  minHeight: '32px',
  flexShrink: 0,
  maxWidth: '100%',
}

function FooterContactIcon({ children }: { children: ReactNode }) {
  return <div style={FOOTER_CONTACT_ICON}>{children}</div>
}

const FOOTER_SOLUTION_LINK_BASE: CSSProperties = {
  display: 'block',
  width: '100%',
  maxWidth: '100%',
  overflowWrap: 'break-word',
  wordBreak: 'normal',
}

function FooterSolutionLink({
  item,
  style,
}: {
  item: ServiceItem
  style: CSSProperties
}) {
  const label = item.title?.trim() || 'Service'
  const target = serviceCardTargetFromItem(item)
  const linkStyle: CSSProperties = {
    ...FOOTER_SOLUTION_LINK_BASE,
    ...style,
  }

  if (target?.type === 'internal') {
    return (
      <Link
        to={target.to}
        onClick={() => window.scrollTo(0, 0)}
        className="footer-link-hover"
        style={linkStyle}
        title={label}
      >
        {label}
      </Link>
    )
  }

  if (target?.type === 'external') {
    return (
      <a
        href={target.href}
        className="footer-link-hover"
        style={linkStyle}
        title={label}
        {...(target.href.startsWith('http')
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {label}
      </a>
    )
  }

  return (
    <a href="#contact" className="footer-link-hover" style={linkStyle} title={label}>
      {label}
    </a>
  )
}

function FooterSolutionLinks({
  items,
  linkStyle,
  gap = '16px',
}: {
  items: ServiceItem[]
  linkStyle: CSSProperties
  gap?: string
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap,
        width: '100%',
      }}
    >
      {items.map((item) => (
        <FooterSolutionLink key={`footer-svc-${item.id}-${item.title}`} item={item} style={linkStyle} />
      ))}
    </div>
  )
}

function FooterContactRows({
  email,
  phone,
  address,
  phoneHref,
  textStyle,
}: {
  email: string
  phone: string
  address: string
  phoneHref: string
  textStyle: CSSProperties
}) {
  const labelStyle: CSSProperties = {
    ...textStyle,
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: '100%',
    ...cmsEllipsis(),
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        width: 'max-content',
        maxWidth: '100%',
      }}
    >
      {email ? (
        <a
          href={`mailto:${email}`}
          style={{ ...FOOTER_CONTACT_ROW, textDecoration: 'none', cursor: 'pointer' }}
        >
          <FooterContactIcon>
            <Mail size={18.29} color={colors.neutral[95]} strokeWidth={1.14} />
          </FooterContactIcon>
          <span style={labelStyle}>{email}</span>
        </a>
      ) : null}
      {phone ? (
        <a
          href={phoneHref}
          style={{ ...FOOTER_CONTACT_ROW, textDecoration: 'none', cursor: 'pointer' }}
        >
          <FooterContactIcon>
            <Phone size={18.29} color={colors.neutral[95]} strokeWidth={1.14} />
          </FooterContactIcon>
          <span style={labelStyle}>{phone}</span>
        </a>
      ) : null}
      {address ? (
        <div style={FOOTER_CONTACT_ROW}>
          <FooterContactIcon>
            <MapPin size={18.29} color={colors.neutral[95]} strokeWidth={1.14} />
          </FooterContactIcon>
          <span style={labelStyle}>{address}</span>
        </div>
      ) : null}
    </div>
  )
}

export default function Footer({ isMobile, cmsReady = true, contactInfo, serviceItems }: FooterProps) {
  const email = cmsContactField(contactInfo?.email, 'info@digitrek.ae', cmsReady)
  const phone = cmsContactField(contactInfo?.phone, '+971 50 1234567', cmsReady)
  const address = cmsContactField(contactInfo?.address, 'Dubai-United Arab Emirates', cmsReady)
  const phoneHref = phone ? `tel:${phone.replace(/\s+/g, '')}` : '#'

  const [fetchedServiceItems, setFetchedServiceItems] = useState<ServiceItem[]>([])

  useEffect(() => {
    if (serviceItems?.length) return
    let cancelled = false
    void fetchServiceSectionItems().then((items) => {
      if (!cancelled) setFetchedServiceItems(items)
    })
    return () => {
      cancelled = true
    }
  }, [serviceItems])

  const solutionItems = useMemo(() => {
    const fromProps = (serviceItems ?? []).filter((item) => item.title?.trim())
    if (fromProps.length) return fromProps
    const fromFetch = fetchedServiceItems.filter((item) => item.title?.trim())
    if (fromFetch.length) return fromFetch
    return cmsReady ? FALLBACK_FOOTER_SERVICES : []
  }, [serviceItems, fetchedServiceItems, cmsReady])
  if (isMobile) {
    return (
      <footer
        id="footer"
        style={{
          width: '100%',
          padding: '48px 16px 32px',
          background: colors.background.white,
        }}
      >
        <div
          style={{
            maxWidth: '360px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <img
              src="/images/Asset 1 1 (2).svg"
              alt="DIGITREK Logo"
              width={48}
              height={52}
              style={{ width: '48px', height: '52px' }}
            />
            <div
              style={{
                fontFamily: "'Segoe UI', sans-serif",
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '32px',
                letterSpacing: '2px',
                color: '#141218',
              }}
            >
              DIGITREK
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
              <h3
                style={{
                  fontFamily: "'Segoe UI', sans-serif",
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '21px',
                  color: colors.neutral[6],
                  marginBottom: '12px',
                }}
              >
                Navigation
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {[
                  { label: 'Home', href: '/' },
                  { label: 'Our Process', href: '/#process' },
                  { label: 'Our Services', href: '/services/odoo-erp' },
                  { label: 'About Us', href: '/#about' },
                  { label: 'Testimonials', href: '/#testimonials' },
                  { label: 'Contact Us', href: '/#contact' },
                ].map((item) => {
                  const isRoute = item.href.startsWith('/') && !item.href.includes('#')

                  return isRoute ? (
                    <Link
                      key={item.label}
                      to={item.href}
                      onClick={() => window.scrollTo(0, 0)}
                      className="footer-link-hover"
                      style={{
                        fontFamily: "'Segoe UI', sans-serif",
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '19px',
                        color: colors.neutral[50],
                        textDecoration: 'none',
                      }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <a
                      key={item.label}
                      href={item.href}
                      className="footer-link-hover"
                      style={{
                        fontFamily: "'Segoe UI', sans-serif",
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '19px',
                        color: colors.neutral[50],
                        textDecoration: 'none',
                      }}
                    >
                      {item.label}
                    </a>
                  )
                })}
              </div>
            </div>

            <div>
              <h3
                style={{
                  fontFamily: "'Segoe UI', sans-serif",
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '21px',
                  color: colors.neutral[6],
                  marginBottom: '12px',
                }}
              >
                Our Services
              </h3>
              <FooterSolutionLinks
                items={solutionItems}
                gap="14px"
                linkStyle={{
                  fontFamily: "'Segoe UI', sans-serif",
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '19px',
                  color: colors.neutral[50],
                  textDecoration: 'none',
                }}
              />
            </div>

            <div>
              <FooterContactRows
                email={email}
                phone={phone}
                address={address}
                phoneHref={phoneHref}
                textStyle={{
                  fontFamily: "'Segoe UI', sans-serif",
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '19px',
                  color: colors.neutral[6],
                }}
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              paddingTop: '8px',
              borderTop: `1px solid ${colors.neutral[90]}`,
            }}
          >
            <div
              style={{
                fontFamily: "'Segoe UI', sans-serif",
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '19px',
                textAlign: 'center',
                color: colors.neutral[50],
              }}
            >
              © 2026 All Rights Reserved.
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <a
                href="https://www.linkedin.com/company/digitrek-ae"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '39px',
                  height: '39px',
                  border: `1px solid ${colors.neutral[50]}`,
                  borderRadius: '50%',
                  textDecoration: 'none',
                }}
              >
                <img
                  src="/images/SVGs/Group 14.svg"
                  alt="LinkedIn"
                  style={{ width: '14px', height: '14px' }}
                />
              </a>
            </div>
          </div>
        </div>
      </footer>
    )
  }

  return (
    <footer
      style={{
        position: 'relative',
        width: '100%',
        height: '608px',
        background: colors.background.white,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          padding: '0px',
          gap: '72px',
          position: 'relative',
          width: '1680px',
          height: '440px',
          margin: '0 auto',
          top: '72px',
        }}
      >
        {/* Main Content */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '0px',
            gap: '48px',
            width: '1680px',
            height: '329px',
          }}
        >
          {/* Columns */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              padding: '0px',
              width: '1680px',
              height: '280px',
            }}
          >
            {/* Logo Column */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '0px',
                gap: '22px',
                width: '147px',
                height: '159px',
              }}
            >
              <img
                src="/images/Asset 1 1 (2).svg"
                alt="DIGITREK Logo"
                style={{
                  width: '88px',
                  height: '94px',
                }}
              />
              <div
                style={{
                  fontFamily: "'Segoe UI', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '32px',
                  lineHeight: '43px',
                  letterSpacing: '2px',
                  color: colors.neutral[6],
                  width: '147px',
                  height: '43px',
                }}
              >
                DIGITREK
              </div>
            </div>

            {/* Navigation Column */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '16px',
                width: '141px',
                height: '172px',
              }}
            >
              <h3
                style={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '24px',
                  color: colors.neutral[6],
                  margin: 0,
                  width: '141px',
                  height: '24px',
                }}
              >
                Navigation
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '0px',
                  gap: '12px',
                  width: '89px',
                  height: '132px',
                }}
              >
                {[
                  { label: 'Home', href: '/' },
                  { label: 'Solutions', href: '/services/odoo-erp' },
                  { label: 'About Us', href: '/#about' },
                  { label: 'Contact', href: '/#contact' },
                ].map((link) => {
                  const isRoute = link.href.startsWith('/') && !link.href.includes('#')

                  return isRoute ? (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => window.scrollTo(0, 0)}
                      className="footer-link-hover"
                      style={{
                        fontFamily: "'Instrument Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 600,
                        fontSize: '20px',
                        lineHeight: '24px',
                        color: colors.neutral[50],
                        textDecoration: 'none',
                        height: '24px',
                      }}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.href}
                      href={link.href}
                      className="footer-link-hover"
                      style={{
                        fontFamily: "'Instrument Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 600,
                        fontSize: '20px',
                        lineHeight: '24px',
                        color: colors.neutral[50],
                        textDecoration: 'none',
                        height: '24px',
                      }}
                    >
                      {link.label}
                    </a>
                  )
                })}
              </div>
            </div>

            {/* Solutions Column */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '16px',
                width: '388px',
                minHeight: '280px',
              }}
            >
              <h3
                style={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '24px',
                  color: colors.neutral[6],
                  margin: 0,
                  width: '388px',
                  height: '24px',
                }}
              >
                Solutions
              </h3>
              <FooterSolutionLinks
                items={solutionItems}
                gap="16px"
                linkStyle={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '24px',
                  color: colors.neutral[50],
                  textDecoration: 'none',
                }}
              />
            </div>

            {/* Talk To Us Column */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '16px',
                flex: '0 1 auto',
                minWidth: '305px',
                maxWidth: '520px',
              }}
            >
              <h3
                style={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '24px',
                  color: colors.neutral[6],
                  margin: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                Talk To Us
              </h3>
              <FooterContactRows
                email={email}
                phone={phone}
                address={address}
                phoneHref={phoneHref}
                textStyle={{
                  fontFamily: "'Instrument Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '24px',
                  color: colors.neutral[30],
                }}
              />
            </div>
          </div>

          {/* Divider */}
          <div
            style={{
              width: '1680px',
              height: '1px',
              background: colors.neutral[6],
            }}
          />
        </div>

        {/* Bottom Section */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            padding: '0px',
            gap: '581px',
            width: '954px',
            height: '39px',
          }}
        >
          {/* Copyright */}
          <div
            style={{
              fontFamily: "'Instrument Sans', sans-serif",
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '26px',
              textAlign: 'center',
              color: colors.neutral[6],
              width: '303px',
              height: '30px',
            }}
          >
            © 2026 All Rights Reserved.
          </div>

          {/* Social Icons */}
          <div
            style={{
              display: 'flex',
              gap: '14px',
              alignItems: 'center',
              width: '39px',
              height: '39px',
            }}
          >
            {FOOTER_LINKS.social
              .filter((social) => social.name === 'LinkedIn')
              .map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: '39px',
                    height: '39px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: `1px solid ${colors.neutral[6]}`,
                    opacity: 1,
                    borderRadius: '19.5px',
                    transition: 'transform 0.3s ease, opacity 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)'
                    e.currentTarget.style.opacity = '1'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)'
                    e.currentTarget.style.opacity = '0.25'
                  }}

                >
                  <img
                    src={social.icon}
                    alt={social.name}
                    style={{
                      width: '14px',
                      height: '14px',
                    }}
                  />
                </a>
              ))}
          </div>
        </div>
      </div>
    </footer>
  )
}

